// @givmo/fundraisers-module — the sending domain's WIRE (EMC-WEB3): the four
// `/org/{org_ref}/email/sender-identity` operations (EMC-BE6) and their tolerant decode, and nothing
// else. The committed OpenAPI snapshot carries none of them yet, so the calls use the configured
// `givmoClient` hatch (`agent_docs/api-client.md` § A whole ROUTE the snapshot lacks) and inherit the
// whole interceptor stack.
//
// ⚠ WHY THIS IS ITS OWN DIRECTORY. The backend names its DNS domain proof in its own words: the state
// `verified`, the field `verified_at`, the route `…/verify`. The suite vocabulary rail bans that root
// across the module (`packages/suite-terminology/src/scopes.ts`, the `fundraisers` scope) because a
// back-office claim to have verified something is the claim it stops; this directory is excluded there,
// as employer-module's DNS-proof Security panels are. So the wire's words stop HERE: the decode
// translates the state to the module's own (`ready`), and nothing outside this directory names it.
//
// ⚠ EVERY FACT IS THE SERVER'S. Whether a campaign email may be scheduled is `can_schedule`, never a
// state this file reads into it; a record's status is the server's word, and an unknown one reads as
// not checked.

import { givmoClient, isGivmoError } from '@givmo/api-client';

/** Where the organization's sending domain stands, in the module's words. */
export type SenderState = 'not_set_up' | 'pending' | 'ready' | 'lapsed';

/** `GET …/sender-identity`, decoded. */
export interface SenderIdentity {
  state: SenderState;
  /** Whether a campaign email may be scheduled now: only a domain that passed sends (D-EMC-5). */
  canSchedule: boolean;
  domain: string | null;
  fromLocalPart: string | null;
  fromAddress: string | null;
  displayName: string | null;
  replyTo: string | null;
  postalAddress: string | null;
  lastCheckedAt: string | null;
  /**
   * Where the address picker starts, from the organization's own profile: its website's domain and
   * its contact email (`null` where it has none a setup would accept). Suggestions only.
   */
  suggestedDomain: string | null;
  suggestedReplyTo: string | null;
}

export type SenderRecordStatus = 'valid' | 'not_valid' | 'not_checked' | 'needed';

/** One DNS record the organization adds. `host` is the full name the server sends. */
export interface SenderRecord {
  key: string;
  type: 'cname' | 'txt';
  host: string;
  value: string;
  status: SenderRecordStatus;
  reason: string | null;
}

export type SenderDmarcStatus = 'needed' | 'exists' | 'unknown';

/** `GET …/sender-identity/records` (and a check's answer), decoded. */
export interface SenderRecords {
  state: SenderState;
  canSchedule: boolean;
  domain: string | null;
  /**
   * The DNS zone the records are added in: the sending domain's registrable domain (eTLD+1, the
   * server's Public Suffix List), so `givmocharitable.org` for `mail.givmocharitable.org`. An older server
   * names none: read as the domain, which is right for every apex sender.
   */
  zone: string | null;
  records: SenderRecord[];
  /**
   * `foundAt`: where an existing record was found (`exists` only), which for a subdomain sender may
   * be the organizational domain's `_dmarc` name. An older server names none.
   */
  dmarc: { status: SenderDmarcStatus; host: string; foundAt: string | null } | null;
  lastCheckedAt: string | null;
  providerDomainMissing: boolean;
}

/** `POST …/sender-identity`'s body. */
export interface SenderIdentityInput {
  domain: string;
  from_local_part: string;
  display_name: string;
  reply_to: string;
  postal_address: string;
  confirm_domain_change?: boolean;
}

/** The server's state words → the module's. An unknown one is no state: nothing is guessed. */
const STATES: Readonly<Record<string, SenderState>> = {
  not_set_up: 'not_set_up',
  pending: 'pending',
  verified: 'ready',
  lapsed: 'lapsed',
};
const RECORD_STATUSES: readonly SenderRecordStatus[] = [
  'valid',
  'not_valid',
  'not_checked',
  'needed',
];
const DMARC_STATUSES: readonly SenderDmarcStatus[] = ['needed', 'exists', 'unknown'];

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function state(value: unknown): SenderState | null {
  return typeof value === 'string' && Object.hasOwn(STATES, value) ? STATES[value]! : null;
}

export function decodeSenderIdentity(body: unknown): SenderIdentity | null {
  const row = record(body);
  const read = row ? state(row.state) : null;
  if (!row || read === null) return null;
  return {
    state: read,
    // `=== true`: an unreadable value never unlocks scheduling.
    canSchedule: row.can_schedule === true,
    domain: text(row.domain),
    fromLocalPart: text(row.from_local_part),
    fromAddress: text(row.from_address),
    displayName: text(row.display_name),
    replyTo: text(row.reply_to),
    postalAddress: text(row.postal_address),
    lastCheckedAt: text(row.last_checked_at),
    suggestedDomain: text(row.suggested_domain),
    suggestedReplyTo: text(row.suggested_reply_to),
  };
}

function decodeRecord(value: unknown): SenderRecord | null {
  const row = record(value);
  const host = row ? text(row.host) : null;
  const data = row ? text(row.value) : null;
  const key = row ? text(row.key) : null;
  if (!row || host === null || data === null || key === null) return null;
  return {
    key,
    type: row.type === 'txt' ? 'txt' : 'cname',
    host,
    value: data,
    status: RECORD_STATUSES.includes(row.status as SenderRecordStatus)
      ? (row.status as SenderRecordStatus)
      : 'not_checked',
    reason: text(row.reason),
  };
}

export function decodeSenderRecords(body: unknown): SenderRecords | null {
  const row = record(body);
  const read = row ? state(row.state) : null;
  if (!row || read === null) return null;
  const dmarc = record(row.dmarc);
  const dmarcHost = dmarc ? text(dmarc.host) : null;
  return {
    state: read,
    canSchedule: row.can_schedule === true,
    domain: text(row.domain),
    zone: text(row.zone) ?? text(row.domain),
    records: Array.isArray(row.records)
      ? row.records.map(decodeRecord).filter((item): item is SenderRecord => item !== null)
      : [],
    dmarc:
      dmarc && dmarcHost !== null
        ? {
            status: DMARC_STATUSES.includes(dmarc.status as SenderDmarcStatus)
              ? (dmarc.status as SenderDmarcStatus)
              : 'unknown',
            host: dmarcHost,
            foundAt: text(dmarc.found_at),
          }
        : null,
    lastCheckedAt: text(row.last_checked_at),
    providerDomainMissing: row.provider_domain_missing === true,
  };
}

function base(orgRef: string): string {
  return `/org/${encodeURIComponent(orgRef)}/email/sender-identity`;
}

/** A route miss is an answer: email campaigns are dark here. */
/**
 * The two campaign-email refusals whose served words name the wire's own root, in the module's words
 * (review-polish A-1): the email's sending domain can't send yet, or the person's own sign-in address
 * isn't proven yet (a test goes only there). Null for any other refusal code.
 */
export type SenderRefusal = 'sender_not_ready' | 'test_address_unconfirmed';
const SENDER_REFUSALS: ReadonlyMap<string, SenderRefusal> = new Map([
  ['EMAIL_CAMPAIGN_SENDER_NOT_VERIFIED', 'sender_not_ready'],
  ['EMAIL_CAMPAIGN_TEST_RECIPIENT_UNVERIFIED', 'test_address_unconfirmed'],
]);
export function senderRefusal(code: string | null): SenderRefusal | null {
  return code === null ? null : (SENDER_REFUSALS.get(code) ?? null);
}

export function isSenderUnavailable(error: unknown): boolean {
  return isGivmoError(error) && error.status === 404;
}

export async function fetchSenderIdentity(
  orgRef: string,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: base(orgRef),
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

export async function fetchSenderRecords(
  orgRef: string,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: `${base(orgRef)}/records`,
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

export async function createSenderIdentity(
  orgRef: string,
  body: SenderIdentityInput,
): Promise<unknown> {
  const { data } = await givmoClient.post({ url: base(orgRef), body, throwOnError: true });
  return data;
}

/** Ask the mail provider to check the records now (the backend's `…/verify`). */
export async function checkSenderRecords(orgRef: string): Promise<unknown> {
  const { data } = await givmoClient.post({ url: `${base(orgRef)}/verify`, throwOnError: true });
  return data;
}
